// src/helpers/pdfOrdenPagoRemito.js
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
// import moment from "moment";
import { getOrdenPagoRemitoById } from "./rutaOrdenPagoRemitos";
import {
  fmtDate,
  fmtNum,
  S,
  loadImageAsDataURL,
  drawHeader,
} from "./pdfCommonSanPancracio";

const $ = (n) => fmtNum(n);

// 🔹 Defaults iguales para todas las OPR (San Pancracio)
const DEFAULT_LOGO_URL = "/SanPancracio.png"; // archivo en /public
const DEFAULT_EMPRESA = {
  nombre: "SAN PANCRACIO SRL",
  linea1: "Avda. Peron 1600 - Oficina 29 Piso 1 - Yerba Buena - Tucumán",
  linea2: "Tel: 0381-3008829 • contacto@sanpancracio.com",
};

// ---- helpers específicos de OPR ----
const origenPago = (p) => {
  const medio = String(p?.medio || "").toLowerCase();

  if (medio === "efectivo") {
    const desc = p?.caja?.descripcion;
    return desc ? `Caja: ${desc}` : (p?.caja ? `Caja: ${p.caja}` : "-");
  }

  if (medio === "transferencia") {
    const c = p?.cuentaBancaria;
    const etiqueta = c?.alias || c?.numero || "";
    return etiqueta
      ? `Cuenta: ${etiqueta}`
      : (p?.cuentaBancaria ? `Cuenta: ${p.cuentaBancaria}` : "-");
  }

  if (medio === "cheque") {
    const nro = p?.referencia || p?.nroCheque || "";
    const fv = p?.fechaCheque ? ` — ${fmtDate(p.fechaCheque)}` : "";
    return nro ? `Cheque ${nro}${fv}` : "Cheque";
  }

  if (medio === "icheq") {
    return p?.referencia ? `iCheq ${p.referencia}` : "iCheq";
  }

  return "-";
};

const clienteNombre = (op) =>
  op?.cliente?.razonsocial ||
  op?.aplicaciones?.find((a) => a?.remito?.cliente)?.remito?.cliente?.razonsocial ||
  "-";

/**
 * Genera PDF de Orden de Pago de Remitos
 * con el mismo encabezado que OCR/Remitos (logo + datos empresa).
 *
 * Puede recibir opcionalmente opts = { logoUrl, empresa }
 */
export async function generarPDFOrdenPagoRemito(id, opts = {}) {
  const r = await getOrdenPagoRemitoById(id);
  const op = r?.orden;
  if (!op) return alert("No se encontró la orden.");

  const logoUrl = opts.logoUrl || DEFAULT_LOGO_URL;
  const empresa = { ...DEFAULT_EMPRESA, ...(opts.empresa || {}) };

  let logoDataURL = null;
  try {
    if (logoUrl) {
      logoDataURL = await loadImageAsDataURL(logoUrl);
    }
  } catch (e) {
    console.error("Error cargando logo OPR:", e);
  }

  const doc = new jsPDF({ unit: "mm", format: "a4" });

  // ----- ENCABEZADO -----
  const yTop = 12;

  // Header solo con nombre
  const empresaSoloNombre = { ...empresa, linea1: "", linea2: "" };
  drawHeader(doc, yTop, logoDataURL, empresaSoloNombre);

  // Dibujamos linea1 y linea2 un poco más abajo
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  if (empresa.linea1) {
    doc.text(empresa.linea1, 105, yTop + 12, { align: "center" });
  }
  if (empresa.linea2) {
    doc.text(empresa.linea2, 105, yTop + 17, { align: "center" });
  }

  // Misma separación que remito / OCR
  let y = yTop + 34;

  // ----- TÍTULO + DATOS GENERALES -----
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.text("ORDEN DE PAGO (REMITOS)", 105, y, { align: "center" });
  y += 8;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);

  const provNombre = S(op?.proveedor?.razonsocial || "-");
  const cliNom = S(clienteNombre(op));

  doc.text(`N°: ${op.numero || "-"}`, 12, y);
  doc.text(`Fecha: ${fmtDate(op.fecha)}`, 198, y, { align: "right" });
  y += 5;
  doc.text(`Proveedor: ${provNombre}`, 12, y);
  y += 5;
  doc.text(`Cliente: ${cliNom}`, 12, y);
  y += 8;

  // ----- TABLA APLICACIONES -----
  autoTable(doc, {
    startY: y,
    margin: { left: 12, right: 12 },
    head: [["Remito", "Pactado", "Pagado ant.", "Aplicado"]],
    body: (op.aplicaciones || []).map((a) => [
      `#${String(a?.remito?.codrem || "").padStart(6, "0")}`,
      `$ ${$(a?.pactado)}`,
      `$ ${$(a?.pagadoAnterior)}`,
      `$ ${$(a?.aplicado)}`,
    ]),
    styles: { fontSize: 9, halign: "right" },
    headStyles: { fillColor: [119, 136, 153], halign: "center" },
    columnStyles: { 0: { halign: "left" } },
  });

  let fy = doc.lastAutoTable?.finalY + 8 || y + 8;

  // ----- TABLA PAGOS -----
  autoTable(doc, {
    startY: fy,
    margin: { left: 12, right: 12 },
    head: [["Medio", "Origen / Datos", "Importe"]],
    body: (op.pagos || []).map((p) => [
      String(p?.medio || "").toUpperCase(),
      origenPago(p),
      `$ ${$(p?.importe)}`,
    ]),
    styles: { fontSize: 9, halign: "right" },
    headStyles: { fillColor: [119, 136, 153], halign: "center" },
    columnStyles: { 0: { halign: "left" }, 1: { halign: "left" } },
  });
  fy = doc.lastAutoTable?.finalY + 8 || fy + 8;

  // ----- TABLA RETENCIONES -----
  if ((op.retenciones || []).length) {
    autoTable(doc, {
      startY: fy,
      margin: { left: 12, right: 12 },
      head: [["Tipo", "Base", "%", "Importe", "Comp."]],
      body: (op.retenciones || []).map((rr) => [
        String(rr?.tipo || "").toUpperCase(),
        `$ ${$(rr?.base)}`,
        Number(rr?.porcentaje || 0).toLocaleString("es-AR"),
        `$ ${$(rr?.importe)}`,
        rr?.comprobante || "",
      ]),
      styles: { fontSize: 9, halign: "right" },
      headStyles: { fillColor: [119, 136, 153], halign: "center" },
      columnStyles: { 0: { halign: "left" }, 4: { halign: "left" } },
    });
    fy = doc.lastAutoTable?.finalY + 8 || fy + 8;
  }

  // ----- TOTALES -----
  const totalPagos = (op.pagos || []).reduce(
    (a, m) => a + Number(m?.importe || 0),
    0
  );
  const totalRets = (op.retenciones || []).reduce(
    (a, m) => a + Number(m?.importe || 0),
    0
  );
  const aplicado = Number(op?.totales?.aplicado || 0);
  const neto =
    Number(op?.totales?.netoPagado || 0) || (totalPagos - totalRets);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text(`£ Aplicado: $ ${$(aplicado)}`, 12, fy);
  fy += 5;
  doc.text(`£ Pagos: $ ${$(totalPagos)}`, 12, fy);
  fy += 5;
  doc.text(`£ Retenciones: $ ${$(totalRets)}`, 12, fy);
  fy += 5;
  doc.text(`£ Neto pagado: $ ${$(neto)}`, 12, fy);
  fy += 7;

  // ----- OBSERVACIONES -----
  if (op.observaciones) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text("Observaciones:", 12, fy);
    fy += 5;
    const anchoTexto = 186; // 210 - 12 - 12
    const split = doc.splitTextToSize(S(op.observaciones), anchoTexto);
    doc.text(split, 12, fy);
  }

  // ----- PIE -----
  doc.setFont("helvetica", "italic");
  doc.setFontSize(8);
  doc.text(
    "Documento electrónico generado por el sistema San Pancracio — Orden de Pago de Remitos",
    12,
    292
  );

  doc.save(`${op.numero || "OPR"}.pdf`);
}
